/**
 * Extension loader - loads TypeScript extension modules using jiti.
 *
 */

import * as fs from "node:fs";
import { createRequire } from "node:module";
import * as os from "node:os";
import * as path from "node:path";
import { fileURLToPath } from "node:url";
import * as _bundledPiAgentCore from "@kolisachint/hoocode-agent-core";
import * as _bundledPiAi from "@kolisachint/hoocode-ai";
import * as _bundledPiAiOauth from "@kolisachint/hoocode-ai/oauth";
import type { KeyId } from "@kolisachint/hoocode-tui";
import * as _bundledPiTui from "@kolisachint/hoocode-tui";
import { createJiti } from "jiti/static";
// Static imports of packages that extensions may use.
// These MUST be static so Bun bundles them into the compiled binary.
// The virtualModules option then makes them available to extensions.
import * as _bundledTypebox from "typebox";
import * as _bundledTypeboxCompile from "typebox/compile";
import * as _bundledTypeboxValue from "typebox/value";
import { CONFIG_DIR_NAME, getAgentDir, isBunBinary } from "../../config.js";
// NOTE: This import works because loader.ts exports are NOT re-exported from index.ts,
// avoiding a circular dependency. Extensions can import from @kolisachint/hoocode-agent.
import * as _bundledPiCodingAgent from "../../index.js";
import { createEventBus, type EventBus } from "../event-bus.js";
import type { ExecOptions } from "../exec.js";
import { execCommand } from "../exec.js";
import { clearExtensionMcpServers } from "../extension-mcp-servers.js";
import { createSyntheticSourceInfo } from "../source-info.js";
import { buildPluginFactory, discoverPlugins, pluginExtensionPath, withheldCapabilities } from "./plugins/index.js";
import { isRepositorySupplied, shouldWithholdRepositorySupplied } from "./plugins/trust.js";
import type {
	Extension,
	ExtensionAPI,
	ExtensionFactory,
	ExtensionLoadIssue,
	ExtensionRuntime,
	LoadExtensionsResult,
	MessageRenderer,
	ProviderConfig,
	RegisteredCommand,
	ToolDefinition,
} from "./types.js";

/** Modules available to extensions via virtualModules (for compiled Bun binary) */
const VIRTUAL_MODULES: Record<string, unknown> = {
	typebox: _bundledTypebox,
	"typebox/compile": _bundledTypeboxCompile,
	"typebox/value": _bundledTypeboxValue,
	"@sinclair/typebox": _bundledTypebox,
	"@sinclair/typebox/compile": _bundledTypeboxCompile,
	"@sinclair/typebox/value": _bundledTypeboxValue,
	"@kolisachint/hoocode-agent-core": _bundledPiAgentCore,
	"@kolisachint/hoocode-tui": _bundledPiTui,
	"@kolisachint/hoocode-ai": _bundledPiAi,
	"@kolisachint/hoocode-ai/oauth": _bundledPiAiOauth,
	"@kolisachint/hoocode-agent": _bundledPiCodingAgent,
};

const require = createRequire(import.meta.url);

/**
 * Get aliases for jiti (used in Node.js/development mode).
 * In Bun binary mode, virtualModules is used instead.
 */
let _aliases: Record<string, string> | null = null;

function getAliases(): Record<string, string> {
	if (_aliases) return _aliases;

	const __dirname = path.dirname(fileURLToPath(import.meta.url));
	const packageIndex = path.resolve(__dirname, "../..", "index.js");

	const typeboxEntry = require.resolve("typebox");
	const typeboxCompileEntry = require.resolve("typebox/compile");
	const typeboxValueEntry = require.resolve("typebox/value");

	const packagesRoot = path.resolve(__dirname, "../../../../");
	// Prefer the sibling workspace build; fall back to normal package resolution.
	// Returns null when neither exists (an unbuilt workspace checkout), so the
	// specifier is simply left unaliased instead of breaking every extension.
	const resolveWorkspaceOrImport = (workspaceRelativePath: string, specifier: string): string | null => {
		const workspacePath = path.join(packagesRoot, workspaceRelativePath);
		if (fs.existsSync(workspacePath)) {
			return workspacePath;
		}
		// require.resolve, not import.meta.resolve: the latter is undefined once
		// this module goes through a bundler/test transform, and throwing here
		// would take down extensions that never import this specifier.
		try {
			return require.resolve(specifier);
		} catch {
			return null;
		}
	};

	_aliases = {
		"@kolisachint/hoocode-agent": packageIndex,
		typebox: typeboxEntry,
		"typebox/compile": typeboxCompileEntry,
		"typebox/value": typeboxValueEntry,
		"@sinclair/typebox": typeboxEntry,
		"@sinclair/typebox/compile": typeboxCompileEntry,
		"@sinclair/typebox/value": typeboxValueEntry,
	};

	const workspaceAliases: Array<[string, string, string]> = [
		["@kolisachint/hoocode-agent-core", "agent/dist/index.js", "@kolisachint/hoocode-agent-core"],
		["@kolisachint/hoocode-tui", "tui/dist/index.js", "@kolisachint/hoocode-tui"],
		["@kolisachint/hoocode-ai", "ai/dist/index.js", "@kolisachint/hoocode-ai"],
		["@kolisachint/hoocode-ai/oauth", "ai/dist/oauth.js", "@kolisachint/hoocode-ai/oauth"],
	];
	for (const [alias, workspaceRelativePath, specifier] of workspaceAliases) {
		const entry = resolveWorkspaceOrImport(workspaceRelativePath, specifier);
		if (entry) {
			_aliases[alias] = entry;
		}
	}

	return _aliases;
}

const UNICODE_SPACES = /[\u00A0\u2000-\u200A\u202F\u205F\u3000]/g;

function normalizeUnicodeSpaces(str: string): string {
	return str.replace(UNICODE_SPACES, " ");
}

function expandPath(p: string): string {
	const normalized = normalizeUnicodeSpaces(p);
	if (normalized.startsWith("~/")) {
		return path.join(os.homedir(), normalized.slice(2));
	}
	if (normalized.startsWith("~")) {
		return path.join(os.homedir(), normalized.slice(1));
	}
	return normalized;
}

function resolvePath(extPath: string, cwd: string): string {
	const expanded = expandPath(extPath);
	if (path.isAbsolute(expanded)) {
		return expanded;
	}
	return path.resolve(cwd, expanded);
}

type HandlerFn = (...args: unknown[]) => Promise<unknown>;

/**
 * Create a runtime with throwing stubs for action methods.
 * Runner.bindCore() replaces these with real implementations.
 */
export function createExtensionRuntime(): ExtensionRuntime {
	const notInitialized = () => {
		throw new Error("Extension runtime not initialized. Action methods cannot be called during extension loading.");
	};
	const state: { staleMessage?: string } = {};
	const assertActive = () => {
		if (state.staleMessage) {
			throw new Error(state.staleMessage);
		}
	};

	const runtime: ExtensionRuntime = {
		sendMessage: notInitialized,
		sendUserMessage: notInitialized,
		appendEntry: notInitialized,
		setSessionName: notInitialized,
		getSessionName: notInitialized,
		setLabel: notInitialized,
		getActiveTools: notInitialized,
		getAllTools: notInitialized,
		setActiveTools: notInitialized,
		// registerTool() is valid during extension load; refresh is only needed post-bind.
		refreshTools: () => {},
		getCommands: notInitialized,
		setModel: () => Promise.reject(new Error("Extension runtime not initialized")),
		getThinkingLevel: notInitialized,
		setThinkingLevel: notInitialized,
		flagValues: new Map(),
		pendingProviderRegistrations: [],
		modeSearchPaths: [],
		assertActive,
		invalidate: (message) => {
			state.staleMessage ??=
				message ??
				"This extension ctx is stale after session replacement or reload. Do not use a captured hoo or command ctx after ctx.newSession(), ctx.fork(), ctx.switchSession(), or ctx.reload(). For newSession, fork, and switchSession, move post-replacement work into withSession and use the ctx passed to withSession. For reload, do not use the old ctx after await ctx.reload().";
		},
		// Pre-bind: queue registrations so bindCore() can flush them once the
		// model registry is available. bindCore() replaces both with direct calls.
		registerProvider: (name, config, extensionPath = "<unknown>") => {
			runtime.pendingProviderRegistrations.push({ name, config, extensionPath });
		},
		unregisterProvider: (name) => {
			runtime.pendingProviderRegistrations = runtime.pendingProviderRegistrations.filter((r) => r.name !== name);
		},
	};

	return runtime;
}

/**
 * Create the ExtensionAPI for an extension.
 * Registration methods write to the extension object.
 * Action methods delegate to the shared runtime.
 */
function createExtensionAPI(
	extension: Extension,
	runtime: ExtensionRuntime,
	cwd: string,
	eventBus: EventBus,
): ExtensionAPI {
	const api = {
		// Registration methods - write to extension
		on(event: string, handler: HandlerFn): void {
			runtime.assertActive();
			const list = extension.handlers.get(event) ?? [];
			list.push(handler);
			extension.handlers.set(event, list);
		},

		registerTool(tool: ToolDefinition): void {
			runtime.assertActive();
			extension.tools.set(tool.name, {
				definition: tool,
				sourceInfo: extension.sourceInfo,
			});
			runtime.refreshTools();
		},

		registerCommand(name: string, options: Omit<RegisteredCommand, "name" | "sourceInfo">): void {
			runtime.assertActive();
			extension.commands.set(name, {
				name,
				sourceInfo: extension.sourceInfo,
				...options,
			});
		},

		registerShortcut(
			shortcut: KeyId,
			options: {
				description?: string;
				handler: (ctx: import("./types.js").ExtensionContext) => Promise<void> | void;
			},
		): void {
			runtime.assertActive();
			extension.shortcuts.set(shortcut, { shortcut, extensionPath: extension.path, ...options });
		},

		registerFlag(
			name: string,
			options: { description?: string; type: "boolean" | "string"; default?: boolean | string },
		): void {
			runtime.assertActive();
			extension.flags.set(name, { name, extensionPath: extension.path, ...options });
			if (options.default !== undefined && !runtime.flagValues.has(name)) {
				runtime.flagValues.set(name, options.default);
			}
		},

		registerMessageRenderer<T>(customType: string, renderer: MessageRenderer<T>): void {
			runtime.assertActive();
			extension.messageRenderers.set(customType, renderer as MessageRenderer);
		},

		// Flag access - checks extension registered it, reads from runtime
		getFlag(name: string): boolean | string | undefined {
			runtime.assertActive();
			if (!extension.flags.has(name)) return undefined;
			return runtime.flagValues.get(name);
		},

		addModeSearchPath(dirPath: string): void {
			runtime.assertActive();
			const resolved = resolvePath(dirPath, cwd);
			if (!runtime.modeSearchPaths.includes(resolved)) {
				runtime.modeSearchPaths.push(resolved);
			}
		},

		getModeSearchPaths(): string[] {
			runtime.assertActive();
			return [...runtime.modeSearchPaths];
		},

		// Action methods - delegate to shared runtime
		sendMessage(message, options): void {
			runtime.assertActive();
			runtime.sendMessage(message, options);
		},

		sendUserMessage(content, options): void {
			runtime.assertActive();
			runtime.sendUserMessage(content, options);
		},

		appendEntry(customType: string, data?: unknown): void {
			runtime.assertActive();
			runtime.appendEntry(customType, data);
		},

		setSessionName(name: string): void {
			runtime.assertActive();
			runtime.setSessionName(name);
		},

		getSessionName(): string | undefined {
			runtime.assertActive();
			return runtime.getSessionName();
		},

		setLabel(entryId: string, label: string | undefined): void {
			runtime.assertActive();
			runtime.setLabel(entryId, label);
		},

		exec(command: string, args: string[], options?: ExecOptions) {
			runtime.assertActive();
			return execCommand(command, args, options?.cwd ?? cwd, options);
		},

		getActiveTools(): string[] {
			runtime.assertActive();
			return runtime.getActiveTools();
		},

		getAllTools() {
			runtime.assertActive();
			return runtime.getAllTools();
		},

		setActiveTools(toolNames: string[]): void {
			runtime.assertActive();
			runtime.setActiveTools(toolNames);
		},

		getCommands() {
			runtime.assertActive();
			return runtime.getCommands();
		},

		setModel(model) {
			runtime.assertActive();
			return runtime.setModel(model);
		},

		getThinkingLevel() {
			runtime.assertActive();
			return runtime.getThinkingLevel();
		},

		setThinkingLevel(level) {
			runtime.assertActive();
			runtime.setThinkingLevel(level);
		},

		registerProvider(name: string, config: ProviderConfig) {
			runtime.assertActive();
			runtime.registerProvider(name, config, extension.path);
		},

		unregisterProvider(name: string) {
			runtime.assertActive();
			runtime.unregisterProvider(name, extension.path);
		},

		events: eventBus,
	} as ExtensionAPI;

	return api;
}

async function loadExtensionModule(extensionPath: string) {
	const jiti = createJiti(import.meta.url, {
		moduleCache: false,
		// In Bun binary: use virtualModules for bundled packages (no filesystem resolution)
		// Also disable tryNative so jiti handles ALL imports (not just the entry point)
		// In Node.js/dev: use aliases to resolve to node_modules paths
		...(isBunBinary ? { virtualModules: VIRTUAL_MODULES, tryNative: false } : { alias: getAliases() }),
	});

	const module = await jiti.import(extensionPath, { default: true });
	const factory = module as ExtensionFactory;
	return typeof factory !== "function" ? undefined : factory;
}

/**
 * Create an Extension object with empty collections.
 */
function createExtension(extensionPath: string, resolvedPath: string): Extension {
	const source =
		extensionPath.startsWith("<") && extensionPath.endsWith(">")
			? extensionPath.slice(1, -1).split(":")[0] || "temporary"
			: "local";
	const baseDir = extensionPath.startsWith("<") ? undefined : path.dirname(resolvedPath);

	return {
		path: extensionPath,
		resolvedPath,
		sourceInfo: createSyntheticSourceInfo(extensionPath, { source, baseDir }),
		handlers: new Map(),
		tools: new Map(),
		messageRenderers: new Map(),
		commands: new Map(),
		flags: new Map(),
		shortcuts: new Map(),
	};
}

async function loadExtension(
	extensionPath: string,
	cwd: string,
	eventBus: EventBus,
	runtime: ExtensionRuntime,
): Promise<{ extension: Extension | null; error: string | null }> {
	const resolvedPath = resolvePath(extensionPath, cwd);

	try {
		const factory = await loadExtensionModule(resolvedPath);
		if (!factory) {
			return { extension: null, error: `Extension does not export a valid factory function: ${extensionPath}` };
		}

		const extension = createExtension(extensionPath, resolvedPath);
		const api = createExtensionAPI(extension, runtime, cwd, eventBus);
		await factory(api);

		return { extension, error: null };
	} catch (err) {
		const message = err instanceof Error ? err.message : String(err);
		return { extension: null, error: `Failed to load extension: ${message}` };
	}
}

/**
 * Create an Extension from an inline factory function.
 */
export async function loadExtensionFromFactory(
	factory: ExtensionFactory,
	cwd: string,
	eventBus: EventBus,
	runtime: ExtensionRuntime,
	extensionPath = "<inline>",
	displayName?: string,
): Promise<Extension> {
	const effectivePath = displayName ?? extensionPath;
	const extension = createExtension(effectivePath, extensionPath);
	if (displayName) {
		extension.sourceInfo = createSyntheticSourceInfo(displayName, {
			source: "inline",
			scope: "temporary",
			origin: "top-level",
		});
	}
	extension.displayName = displayName;
	extension.internal = factory.internal === true;
	const api = createExtensionAPI(extension, runtime, cwd, eventBus);
	await factory(api);
	return extension;
}

/**
 * Load extensions from paths.
 */
export async function loadExtensions(paths: string[], cwd: string, eventBus?: EventBus): Promise<LoadExtensionsResult> {
	const extensions: Extension[] = [];
	const errors: Array<{ path: string; error: string }> = [];
	const resolvedEventBus = eventBus ?? createEventBus();
	const runtime = createExtensionRuntime();

	for (const extPath of paths) {
		const { extension, error } = await loadExtension(extPath, cwd, resolvedEventBus, runtime);

		if (error) {
			errors.push({ path: extPath, error });
			continue;
		}

		if (extension) {
			extensions.push(extension);
		}
	}

	return {
		extensions,
		errors,
		runtime,
	};
}

interface HooCodeManifest {
	extensions?: string[];
	themes?: string[];
	skills?: string[];
	prompts?: string[];
}

/**
 * Read a package's resource manifest.
 *
 * `hoocode` is the field. `pi` is a deprecated alias kept for packages authored
 * against the upstream project this one grew out of: dropping it would break
 * third-party packages that are still published and still work, and the cost of
 * keeping it is one branch. It is not a rename in progress -- nothing in this
 * repository writes `pi`, the scaffolders emit `hoocode`, and a package that
 * declares both gets `hoocode`.
 */
function readHooCodeManifest(packageJsonPath: string): HooCodeManifest | null {
	try {
		const content = fs.readFileSync(packageJsonPath, "utf-8");
		const pkg = JSON.parse(content);
		if (pkg.hoocode && typeof pkg.hoocode === "object") {
			return pkg.hoocode as HooCodeManifest;
		}
		// Deprecated alias; see above.
		if (pkg.pi && typeof pkg.pi === "object") {
			return pkg.pi as HooCodeManifest;
		}
		return null;
	} catch {
		return null;
	}
}

function isExtensionFile(name: string): boolean {
	return name.endsWith(".ts") || name.endsWith(".js");
}

/**
 * Resolve extension entry points from a directory.
 *
 * Checks for:
 * 1. package.json with a "hoocode.extensions" field -> returns declared paths
 * 2. index.ts or index.js -> returns the index file
 *
 * Returns resolved paths or null if no entry points found.
 */
function resolveExtensionEntries(dir: string): string[] | null {
	// A declared manifest wins over a bare index file.
	const packageJsonPath = path.join(dir, "package.json");
	if (fs.existsSync(packageJsonPath)) {
		const manifest = readHooCodeManifest(packageJsonPath);
		if (manifest?.extensions?.length) {
			const entries: string[] = [];
			for (const extPath of manifest.extensions) {
				const resolvedExtPath = path.resolve(dir, extPath);
				if (fs.existsSync(resolvedExtPath)) {
					entries.push(resolvedExtPath);
				}
			}
			if (entries.length > 0) {
				return entries;
			}
		}
	}

	// Check for index.ts or index.js
	const indexTs = path.join(dir, "index.ts");
	const indexJs = path.join(dir, "index.js");
	if (fs.existsSync(indexTs)) {
		return [indexTs];
	}
	if (fs.existsSync(indexJs)) {
		return [indexJs];
	}

	return null;
}

/**
 * Discover extensions in a directory.
 *
 * Discovery rules:
 * 1. Direct files: `extensions/*.ts` or `*.js` → load
 * 2. Subdirectory with index: `extensions/* /index.ts` or `index.js` → load
 * 3. Subdirectory with package.json: `extensions/* /package.json` with a "hoocode" field → load what it declares
 *
 * No recursion beyond one level. Complex packages must use package.json manifest.
 */
function discoverExtensionsInDir(dir: string): string[] {
	if (!fs.existsSync(dir)) {
		return [];
	}

	const discovered: string[] = [];

	try {
		const entries = fs.readdirSync(dir, { withFileTypes: true });

		for (const entry of entries) {
			const entryPath = path.join(dir, entry.name);

			// 1. Direct files: *.ts or *.js
			if ((entry.isFile() || entry.isSymbolicLink()) && isExtensionFile(entry.name)) {
				discovered.push(entryPath);
				continue;
			}

			// 2 & 3. Subdirectories
			if (entry.isDirectory() || entry.isSymbolicLink()) {
				const entries = resolveExtensionEntries(entryPath);
				if (entries) {
					discovered.push(...entries);
				}
			}
		}
	} catch {
		return [];
	}

	return discovered;
}

/**
 * Discover and load extensions from standard locations.
 */
export async function discoverAndLoadExtensions(
	configuredPaths: string[],
	cwd: string,
	agentDir: string = getAgentDir(),
	eventBus?: EventBus,
): Promise<LoadExtensionsResult> {
	const allPaths: string[] = [];
	const seen = new Set<string>();

	const addPaths = (paths: string[]) => {
		for (const p of paths) {
			const resolved = path.resolve(p);
			if (!seen.has(resolved)) {
				seen.add(resolved);
				allPaths.push(p);
			}
		}
	};

	// 1. Project-local extensions: cwd/${CONFIG_DIR_NAME}/extensions/
	const localExtDir = path.join(cwd, CONFIG_DIR_NAME, "extensions");
	addPaths(discoverExtensionsInDir(localExtDir));

	// 2. Global extensions: agentDir/extensions/
	const globalExtDir = path.join(agentDir, "extensions");
	addPaths(discoverExtensionsInDir(globalExtDir));

	// 3. Explicitly configured paths
	for (const p of configuredPaths) {
		const resolved = resolvePath(p, cwd);
		if (fs.existsSync(resolved) && fs.statSync(resolved).isDirectory()) {
			// Check for package.json with a hoocode manifest, or an index.ts
			const entries = resolveExtensionEntries(resolved);
			if (entries) {
				addPaths(entries);
				continue;
			}
			// No explicit entries - discover individual files in directory
			addPaths(discoverExtensionsInDir(resolved));
			continue;
		}

		addPaths([resolved]);
	}

	const resolvedEventBus = eventBus ?? createEventBus();
	const result = await loadExtensions(allPaths, cwd, resolvedEventBus);

	// Plugins: directories under plugins/ with a recognized manifest.
	const pluginDirs = defaultPluginDirs(cwd, agentDir);
	const pluginResult = await loadPlugins(pluginDirs, cwd, resolvedEventBus, result.runtime);
	result.extensions.push(...pluginResult.extensions);
	result.errors.push(...pluginResult.errors);

	return result;
}

/**
 * Whether a discovered plugin sits in the working tree, and therefore came with
 * the repository as far as anyone but its installer can tell.
 *
 * All three project paths count: `<cwd>/.claude/skills` (the vendor convention
 * for repo-committed plugins) and `<cwd>/.agents/plugins` + `<cwd>/.hoocode/plugins`
 * (hoocode's own project-scope install homes). An earlier version listed only the
 * first, reasoning that the hoocode homes held plugins "the user installed
 * deliberately" — true of the person who ran the install, and false for every
 * collaborator who clones the result. Location cannot tell those two apart, so it
 * is the wrong thing to ask; {@link isWorkspaceTrusted} asks the right one.
 */
export function isProjectSuppliedPlugin(pluginRoot: string, cwd: string): boolean {
	return isRepositorySupplied(pluginRoot, pluginProjectScopeRoots(cwd));
}

/** A plugin's project-scope homes — the locations that travel with a clone. */
function pluginProjectScopeRoots(cwd: string): string[] {
	return [
		path.join(cwd, ".claude", "skills"),
		path.join(cwd, ".agents", "plugins"),
		path.join(cwd, CONFIG_DIR_NAME, "plugins"),
	];
}

/**
 * Whether a plugin's **executable** capabilities (hooks, MCP servers) should be
 * withheld: it lives in the working tree and this machine has not trusted the
 * workspace.
 *
 * Passive capabilities always load. Reading a repository's skill text is what
 * opening the repository already implies; starting its processes is not.
 */
export function shouldWithholdExecutables(pluginRoot: string, cwd: string, agentDir: string = getAgentDir()): boolean {
	return shouldWithholdRepositorySupplied(pluginRoot, cwd, pluginProjectScopeRoots(cwd), agentDir);
}

/**
 * Standard plugin discovery directories, highest precedence first.
 *
 * `.agents/plugins/` is the cross-vendor, primary home and is listed ahead of the
 * `.hoocode/plugins/` fallback at each scope, so an `.agents`-installed plugin
 * wins over a same-id `.hoocode` one (discoverPlugins is first-wins by id).
 * Project scope beats global. The global surfaces live next to the agent dir
 * (`~/.agents`, `~/.claude` alongside `~/.hoocode`), so they stay parameterized
 * on `agentDir` rather than hardcoding the home directory.
 *
 * Two of these are *production homes* for plugins hoocode authored, and two are
 * skills directories:
 *
 * - `<cwd>/.agents/plugins` is the legacy project-local install home. Nothing
 *   writes there any more; it is read so plugins installed by older versions
 *   keep working.
 * - `.claude/skills` (project and personal) implements Claude Code's
 *   skills-directory plugins: a folder there carrying `.claude-plugin/plugin.json`
 *   is a plugin, and a folder with only a `SKILL.md` stays a plain skill —
 *   `parsePluginDir` returns null for the latter, which is exactly the vendor's
 *   own rule, so no special-casing is needed here.
 *
 * See docs/plugin-system-architecture.md §5.3 and §5.7.
 */
export function defaultPluginDirs(cwd: string, agentDir: string = getAgentDir()): string[] {
	const home = path.dirname(agentDir);
	return [
		path.join(cwd, ".agents", "plugins"),
		path.join(cwd, CONFIG_DIR_NAME, "plugins"),
		path.join(cwd, ".claude", "skills"),
		path.join(home, ".agents", "plugins"),
		path.join(home, ".agents", "publish", "github"),
		path.join(home, ".claude", "skills"),
		path.join(agentDir, "plugins"),
	];
}

/**
 * Discover plugins under `pluginDirs` and load each as a synthetic extension into
 * the given runtime/event bus. Clears the extension MCP registry first so reloads
 * rebuild the set cleanly.
 */
export async function loadPlugins(
	pluginDirs: string[],
	cwd: string,
	eventBus: EventBus,
	runtime: ExtensionRuntime,
): Promise<{ extensions: Extension[]; errors: ExtensionLoadIssue[] }> {
	clearExtensionMcpServers();
	const extensions: Extension[] = [];
	const errors: ExtensionLoadIssue[] = [];

	for (const plugin of discoverPlugins(pluginDirs)) {
		try {
			// Withhold the executable half of a plugin that lives in the working tree
			// until this machine has trusted the workspace. Skills, commands and
			// subagents still load — reading a repository's text is what opening it
			// already implies. See PluginFactoryOptions.passiveOnly.
			const passiveOnly = shouldWithholdExecutables(plugin.root, cwd);
			const withheld = passiveOnly ? withheldCapabilities(plugin) : [];
			const extension = await loadExtensionFromFactory(
				buildPluginFactory(plugin, { passiveOnly }),
				cwd,
				eventBus,
				runtime,
				pluginExtensionPath(plugin.id),
				`plugin:${plugin.id}`,
			);
			extensions.push(extension);
			if (withheld.length > 0) {
				// A warning, not an error: the plugin *did* load, minus its executable
				// half, and the session must reach the prompt for `/plugin trust` to be
				// runnable at all. Reporting this as an error aborted startup, which
				// left the only remedy behind a door it had just locked.
				errors.push({
					path: plugin.manifestPath,
					severity: "warning",
					error:
						`Plugin "${plugin.id}" is in the working tree: ${withheld.join(" and ")} not loaded. ` +
						"Code committed to a repository runs for whoever clones it, so hoocode does not start it " +
						"until you say this directory is yours to run code from. Run `/plugin trust` to allow it here, " +
						"or install the plugin at user scope instead.",
				});
			}
		} catch (err) {
			errors.push({
				path: plugin.manifestPath,
				error: `Failed to load plugin "${plugin.id}": ${err instanceof Error ? err.message : String(err)}`,
			});
		}
	}

	return { extensions, errors };
}
